import { useEffect, useState } from "react";

/** Continuous text scaling: a multiplier on the responsive base font size,
 *  stepped by the accessibility panel and persisted as a number. */
export const FONT_SCALE_MIN = 0.8;
export const FONT_SCALE_MAX = 1.6;
const FONT_SCALE_STEP = 0.1;

const clampScale = (n: number): number =>
  Math.min(FONT_SCALE_MAX, Math.max(FONT_SCALE_MIN, Math.round(n * 100) / 100));

// Step from the nearest 10% grid point so the value always lands on
// 80/90/100…%, even after touching a bound or loading an odd stored value
// (prevents the 95/105% drift where you can't get back to 100%).
const stepScale = (s: number, dir: 1 | -1): number =>
  clampScale(Math.round(s * 10) / 10 + dir * FONT_SCALE_STEP);

function initialDark(): boolean {
  const stored = localStorage.getItem("llr_dark_theme");
  if (stored != null) return stored === "true";
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
}

function initialFontScale(): number {
  const s = localStorage.getItem("llr_font_scale");
  if (s != null) {
    const n = parseFloat(s);
    // Snap to the 10% grid so a previously-stuck off-grid value (e.g. 95%)
    // self-corrects on load.
    if (!Number.isNaN(n)) return clampScale(Math.round(n * 10) / 10);
  }
  // Migrate the old 3-step setting (normal/medium/large) to a scale, so an
  // existing resident's text doesn't jump.
  const legacy = localStorage.getItem("llr_text_size");
  if (legacy === "medium") return 1.1;
  if (legacy === "large") return 1.3;
  return 1;
}

function initialUnderline(): boolean {
  return localStorage.getItem("llr_underline_links") === "true";
}

/**
 * Resident accessibility preferences — dark mode, text scale, underline links —
 * persisted to localStorage and applied to <html>.
 */
export function useSettings() {
  const [dark, setDark] = useState<boolean>(initialDark);
  const [fontScale, setFontScale] = useState<number>(initialFontScale);
  const [underlineLinks, setUnderlineLinks] = useState<boolean>(initialUnderline);

  useEffect(() => {
    document.documentElement.classList.toggle("dark-mode", dark);
    localStorage.setItem("llr_dark_theme", String(dark));
  }, [dark]);

  useEffect(() => {
    document.documentElement.style.setProperty("--font-scale", String(fontScale));
    localStorage.setItem("llr_font_scale", String(fontScale));
  }, [fontScale]);

  useEffect(() => {
    document.documentElement.classList.toggle("underline-links", underlineLinks);
    localStorage.setItem("llr_underline_links", String(underlineLinks));
  }, [underlineLinks]);

  const increaseFont = () => setFontScale((s) => stepScale(s, 1));
  const decreaseFont = () => setFontScale((s) => stepScale(s, -1));

  const reset = () => {
    setDark(false);
    setFontScale(1);
    setUnderlineLinks(false);
  };

  return {
    dark,
    setDark,
    fontScale,
    increaseFont,
    decreaseFont,
    underlineLinks,
    setUnderlineLinks,
    reset,
  };
}
